import { z } from "zod";
import { IdSchema, TimestampSchema } from "./primitives.ts";

/**
 * What collided.
 * - `path`: two claims' path globs can match the same file.
 * - `symbol`: two claims name the same symbol (or one nested in the other).
 * - `contract`: two claims name the same contract ref, for example both `schema:tips`.
 * - `migration`: both claims set the migration flag.
 * - `diff`: pushed changes collided, found by comparing diff summaries rather than declarations.
 */
export const OverlapKindSchema = z.enum(["path", "symbol", "contract", "migration", "diff"]);
/** See {@link OverlapKindSchema}. */
export type OverlapKind = z.infer<typeof OverlapKindSchema>;

/**
 * How serious an overlap is.
 * - `block`: stop and resolve through the protocol before writing more code. Landing is refused while open.
 * - `warn`: proceed, but tell the other agent. Does not block landing.
 */
export const OverlapSeveritySchema = z.enum(["warn", "block"]);
/** See {@link OverlapSeveritySchema}. */
export type OverlapSeverity = z.infer<typeof OverlapSeveritySchema>;

/** Whether an overlap came from declared claims or from pushed diffs. */
export const OverlapDetectedBySchema = z.enum(["declared", "diff"]);
/** See {@link OverlapDetectedBySchema}. */
export type OverlapDetectedBy = z.infer<typeof OverlapDetectedBySchema>;

/**
 * Lifecycle of an overlap.
 * - `open`: detected, nobody has proposed anything yet.
 * - `negotiating`: a proposal or counter has been posted against it.
 * - `resolved`: a {@link Resolution} was recorded and the coordinator is enforcing its terms.
 * - `escalated`: handed to a human. Still blocks landing until a human records a resolution.
 * - `released`: one side's claim was released or expired, or one side's session ended,
 *   before it was resolved. No longer blocks anything.
 *
 * The negotiation protocol (packages/protocol) tracks finer states such as proposed, countered
 * and timed out. They map onto these: proposed and countered are `negotiating`; agreed, enforced
 * and timed out (which records a default resolution) are `resolved`.
 */
export const OverlapStatusSchema = z.enum(["open", "negotiating", "resolved", "escalated", "released"]);
/** See {@link OverlapStatusSchema}. */
export type OverlapStatus = z.infer<typeof OverlapStatusSchema>;

/** Statuses in which a `block` overlap still blocks landing. */
export const UNRESOLVED_OVERLAP_STATUSES: readonly OverlapStatus[] = ["open", "negotiating", "escalated"];

/**
 * A detected collision between two sessions' intended or actual work.
 *
 * Side A is always the earlier party (the claim that existed first, or the earlier diff),
 * side B the one whose declaration or push triggered detection.
 */
export const OverlapSchema = z
  .object({
    /** Coordinator-assigned id. Reference it in messages and resolutions. */
    id: IdSchema,
    /**
     * Side A's claim. Null only for `diff` overlaps where side A edited the subject
     * without any claim covering it (an undeclared edit).
     */
    claimA: IdSchema.nullable(),
    /** Side B's claim. Null under the same rule as `claimA`. */
    claimB: IdSchema.nullable(),
    /** Side A's session. Always set, so you know who to message. */
    sessionA: IdSchema,
    /** Side B's session. Always set. */
    sessionB: IdSchema,
    /** What collided, see {@link OverlapKindSchema}. */
    kind: OverlapKindSchema,
    /** Block or warn, see {@link OverlapSeveritySchema}. */
    severity: OverlapSeveritySchema,
    /** Declared claims or pushed diffs. */
    detectedBy: OverlapDetectedBySchema,
    /** Current lifecycle state, see {@link OverlapStatusSchema}. */
    status: OverlapStatusSchema,
    /**
     * The concrete things that collided, for example
     * `["apps/api/migrations/**"]`, `["schema:tips"]` or `["apps/api/src/routes/tips/index.ts"]`.
     */
    subjects: z.array(z.string().min(1)).min(1),
    /** One human-readable sentence explaining the collision. */
    summary: z.string().min(1),
    /** When it was detected. */
    createdAt: TimestampSchema,
    /** When its status last changed. */
    updatedAt: TimestampSchema,
  })
  .refine((o) => o.sessionA !== o.sessionB, { message: "an overlap needs two different sessions", path: ["sessionB"] })
  .refine((o) => o.detectedBy === "diff" || (o.claimA !== null && o.claimB !== null), {
    message: "declared overlaps must reference both claims",
    path: ["claimA"],
  })
  .refine((o) => (o.kind === "diff") === (o.detectedBy === "diff"), {
    message: "kind 'diff' is used exactly when detectedBy is 'diff'",
    path: ["kind"],
  });
/** See {@link OverlapSchema}. */
export type Overlap = z.infer<typeof OverlapSchema>;

/**
 * A potential overlap returned by a dry-run `checkOverlap`. Nothing is recorded.
 * `otherClaimId` is the existing claim you would collide with.
 */
export const OverlapCandidateSchema = z.object({
  /** What would collide. Never `diff`, since a dry run only compares declarations. */
  kind: OverlapKindSchema.exclude(["diff"]),
  /** Block or warn under the severity rules. */
  severity: OverlapSeveritySchema,
  /** The existing claim you would collide with. */
  otherClaimId: IdSchema,
  /** The session that holds it. */
  otherSessionId: IdSchema,
  /** The concrete things that would collide. */
  subjects: z.array(z.string().min(1)).min(1),
});
/** See {@link OverlapCandidateSchema}. */
export type OverlapCandidate = z.infer<typeof OverlapCandidateSchema>;
